import React, { useEffect, useRef, useState } from "react";
import { Flex } from "@chakra-ui/core";
import { useMediaQuery } from "@chakra-ui/react";
import { RfxTabLink } from "app/RfxTabLink";
import { useRfxNavigate } from "app/useRfxNavigate";
import {
	SelectWithLabel,
	type SelectOption,
} from "newui/components/ui/InputWithLabel/SelectWithLabel";
import ButtonViewGroup from "components/ButtonViewGroup/ButtonViewGroup";
import SearchBar from "components/RFQ/SearchBar/SearchBar";
import SortComp from "components/RFQ/SortComp/SortComp";
import { Pagination } from "newui/components/Pagination/Pagination";
import { SkeletonBase } from "newui/components/Skeleton/Skeleton";
import {
	RFX_DEFAULT_SORT,
	RFX_SORT_OPTIONS,
	type RfxSortOption,
} from "./config/rfx-sort-options";
import { useRfxViewMode, type RfxViewMode } from "./hooks/useRfxViewMode";
import { RfxListPageMeta, RfxListQueryFilters } from "./hooks/useRfxList";
import type { RfxListSurface } from "./surfaces/rfx-list-surface";
import type {
	RfxListItemResource,
	RfxListTab,
} from "./types/rfx-list.type";

export interface RfxListPageProps<
	T = RfxListItemResource,
	TTab extends string = RfxListTab,
> {
	/** :companyId route param — required to build tab links. */
	companyId?: string;
	path?: string;
	/** Empty-state content, scoped to the active tab by the caller. */
	renderEmptyState?: () => React.ReactNode;
	/**
	 * Page-level actions rendered top-right, beside the title. Shown on EVERY
	 * tab (they act on the scope, not the tab) and independent of whether the
	 * list has rows — an empty Tenders & Quotes is exactly when "Create RFx"
	 * matters most.
	 */
	headerActions?: React.ReactNode;
	/**
	 * Per-backend row rendering. `view` is owned by this page (the toolbar hosts
	 * the toggle, as legacy `RFQ.js` did) and handed down — list components must
	 * NOT render their own `ButtonViewGroup`.
	 */
	renderList: (
		rows: T[],
		meta: RfxListPageMeta | undefined,
		view: RfxViewMode
	) => React.ReactNode;
	/**
	 * Which list this is: tabs, title, hrefs, default tab and data source. The
	 * shell has no scope switch of its own — see `surfaces/rfx-list-surface.ts`.
	 */
	surface: RfxListSurface<TTab, T>;
	/** :tab route param — the sole source of truth for the active tab. */
	tab?: string;
}

/**
 * Shared header/tab-bar/list/pagination/empty/loading/error shell for all
 * three RFx list pages (T&Q, Approvals, Panel). Driven entirely by
 * tab-config-as-data — no page-specific logic here (see TASK-007 scope cap).
 * Active tab is derived from the route `:tab` param, never local state, so
 * browser back/forward and deep links (e.g. 006's approval emails) work. An
 * invalid/missing tab segment is handled by a declarative `<Redirect>` at the
 * route-registration layer, not here.
 */
export const RfxListPage = <T = RfxListItemResource, TTab extends string = RfxListTab,>({
	companyId,
	headerActions,
	renderEmptyState,
	renderList,
	surface,
	tab,
}: RfxListPageProps<T, TTab>) => {
	const { tabs } = surface;
	// Tab validity is asked of the surface, not matched against a fixed union:
	// the bidder's tab keys are submission states and are not members of the
	// backend-mirrored `RfxListTab`.
	const activeTab = surface.isValidTab(tab) ? tab : surface.defaultTab;

	// Page state is held here as plain state rather than via `usePagination`.
	// That hook clamps `setCurrentPage` against a `totalPages` derived from a
	// `totalItems` it is given up-front — but the total only arrives with the
	// response, and the request needs the page number, so it can never be known
	// at this point in the render. It was previously passed a hardcoded 0,
	// making `totalPages` 0 and silently rejecting EVERY page change.
	//
	// `Pagination` below already bounds-checks against the real `meta.total`
	// before it calls back, so it is the single authority on what page is
	// reachable; this is just where the answer is stored.
	const [currentPage, setCurrentPage] = useState(1);
	// 12 matches the backend's hardcoded PageSize::Default — the wire has no
	// per_page override, so this must track the server, not a UI preference.
	const [rowsPerPage, setRowsPerPage] = useState(12);

	// `SearchBar` debounces internally (300ms) before calling `setState`, so
	// this already holds the committed term — no second debounce needed.
	const [committedSearch, setCommittedSearch] = useState("");
	// Legacy `RFQ.js` defaulted to `sortedData[2]` (Last Activity). PRO-637
	// regressed this to `undefined`; restored here so lists open on
	// most-recently-touched-first rather than alphabetical.
	const [sorted, setSorted] = useState<RfxSortOption>(RFX_DEFAULT_SORT);
	const [openSortPopUp, setOpenSortPopUp] = useState(false);
	const { view, setView } = useRfxViewMode();
	const navigate = useRfxNavigate();

	// Matches the `lg:` breakpoint the toolbar grid already switches on (1024px),
	// so the JS-driven and CSS-driven halves of this layout flip together.
	const [isMobile] = useMediaQuery("(max-width: 1023px)");

	// 14.4: list view is unusable at phone widths (the row is a wide table), so
	// mobile is pinned to cards and the toggle is hidden. `view` state is left
	// untouched — rotating back to desktop restores whatever was chosen there.
	const effectiveView: RfxViewMode = isMobile ? "grid" : view;

	const isFirstSearchRender = useRef(true);
	useEffect(() => {
		// Search/sort reset to page 1 (legacy RfqOverviews pattern) — but not on
		// mount, where `currentPage` already starts at 1.
		if (isFirstSearchRender.current) {
			isFirstSearchRender.current = false;
			return;
		}
		setCurrentPage(1);
		// `activeTab` belongs here: page number is per-tab state, but this component
		// holds ONE value across tab changes. Landing on page 3 of a tab that
		// only has one page returns an empty payload, which reads as "the tab didn't
		// load" rather than "you're past the end".
	}, [committedSearch, sorted, activeTab, setCurrentPage]);

	const filters: RfxListQueryFilters = {
		page: currentPage,
		...(committedSearch ? { search: committedSearch } : {}),
		sort: sorted.value,
	};

	const { rows, meta, counts, isLoading, isError } = surface.useList(
		activeTab,
		filters
	);

	// Declared after the list query — the labels carry the tab counts.
	const mobileTabOptions: SelectOption[] = tabs.map(({ tab: tabKey, label }) => {
		const count = counts[tabKey];

		return {
			label: count === undefined ? label : `${label} (${count})`,
			value: tabKey,
		};
	});

	const renderPagination = (testId: string, className?: string) => (
		<Pagination
			className={className}
			currentPage={currentPage}
			rowsPerPage={rowsPerPage}
			totalItems={meta?.total ?? 0}
			onPageChange={setCurrentPage}
			onRowsPerPageChange={setRowsPerPage}
			testId={testId}
		/>
	);

	return (
		// 14.1: the single-column (mobile) layout has no gutter of its own, so
		// cards ran flush to the viewport edge. Padding goes on the page rather
		// than the cards, so it cannot double up inside the desktop grid.
		<div className="px-4 lg:px-0" data-testid="rfx-list-page">
			{/*
			 * 14.3: on mobile the tab strip collapses to a dropdown pinned above
			 * the title. The horizontal-scroll strip pushed most tabs off-screen
			 * on a phone; a dropdown shows every tab (and its count) at once.
			 */}
			{isMobile && (
				<div className="mb-4">
					<SelectWithLabel
						noLabel
						name="rfx-mobile-tab"
						testId="rfx-list-tab-select"
						options={mobileTabOptions}
						value={
							mobileTabOptions.find(
								(option) => option.value === activeTab
							) ?? null
						}
						onChange={(option) => {
							const selected = option as SelectOption | null;

							if (!companyId || !selected) {
								return;
							}

							navigate(
								surface.buildPath(
									companyId,
									selected.value as TTab
								)
							);
						}}
					/>
				</div>
			)}

			{/*
			 * 14.2: `flex-wrap` — at mobile widths the title and the Create/
			 * Templates buttons cannot share a row, and without wrapping the
			 * buttons were pushed past the right edge and clipped.
			 */}
			<div className="mb-4 flex flex-wrap items-start justify-between gap-3">
				<h1 className="pt-2 text-2xl font-semibold text-royal-blue">
					{surface.title}
				</h1>
				{headerActions && (
					<div
						className="flex flex-wrap items-center gap-3"
						data-testid="rfx-list-header-actions"
					>
						{headerActions}
					</div>
				)}
			</div>

			{/*
			 * Legacy `RFQ.js` toolbar (@ origin/staging:232-292): tabs and
			 * controls are SIBLINGS in one 2-column grid, so on desktop they
			 * share a single row (tabs left, controls right). PRO-637 split
			 * them into separate stacked rows — restored here. The `order-*`
			 * flip keeps controls above the scrollable tab strip on mobile.
			 */}
			<div className="grid w-full place-content-between gap-8 md:grid-cols-1 lg:grid-cols-2 lg:gap-0">
				{/* Hidden on mobile — the dropdown above replaces it there. */}
				<Flex
					hidden={isMobile}
					// `border-b` sits on the tab strip alone, not on the grid row, so
					// the rule never runs on under the search/sort/view controls in
					// the right-hand column. `w-fit` is what makes it stop at the END
					// of the last tab: a flex child stretches to fill its grid column
					// by default, which dragged the rule across the full page width.
					//
					// NO `overflow-x-auto`/`scrollbar` here. `.scrollbar` (index.css)
					// gives a 4px webkit track, which rendered as a grey bar directly
					// above `border-b` — reading as a doubled border. The strip is
					// `hidden` below 1024px anyway (the mobile dropdown replaces it),
					// so there is no width at which it needs to scroll.
					className="order-2 w-fit max-w-full gap-4 border-b border-gray-200 pb-3 lg:order-1 lg:pb-0"
					data-testid="rfx-list-tab-bar"
					role="tablist"
				>
					{tabs.map(({ tab: tabKey, label }) => {
						const isActive = tabKey === activeTab;
						// `counts` covers every tab in the scope; `meta.total` covers
						// only the tab actually fetched. Prefer counts, fall back to
						// meta for the active tab so the badge still renders if an
						// older API build omits the `counts` key.
						const count =
							counts[tabKey] ??
							(isActive ? meta?.total : undefined);

						return (
							<RfxTabLink
								key={tabKey}
								to={
									companyId
										? surface.buildPath(companyId, tabKey)
										: "#"
								}
								isActive={isActive}
								data-testid={`rfx-list-tab-${tabKey}`}
								// `border-b-2` is in the BASE classes so EVERY tab reserves
								// the 2px: the underline is real box height, so applying
								// the border only when active made the active tab taller
								// than its siblings, growing the flex row and shifting the
								// whole page below it by 2px on every tab click.
								//
								// The COLOUR must be an either/or, never both — listing
								// `border-transparent` in the base and `border-royal-blue`
								// in the active branch puts two equal-specificity rules on
								// the element, and the winner is decided by their order in
								// the generated stylesheet, not by their order in this
								// string. That silently ate the active tab's underline.
								className={`flex items-center gap-2 whitespace-nowrap rounded-t border-b-2 px-4 pb-2 pt-2 hover:bg-blue-200 ${
									isActive
										? "border-royal-blue font-medium text-royal-blue"
										: "border-transparent text-gray-500 hover:text-gray-700"
								}`}
							>
								{label}
								{count !== undefined && (
									<span
										className={`rounded-full px-2 py-0.5 text-xs font-medium ${
											isActive
												? "bg-royal-blue text-white"
												: "bg-gray-100 text-gray-600"
										}`}
										data-testid={`rfx-list-tab-count-${tabKey}`}
									>
										{count}
									</span>
								)}
							</RfxTabLink>
						);
					})}
				</Flex>

				<Flex
					className="order-1 gap-4 justify-self-end lg:order-2"
					data-testid="rfx-list-controls"
				>
					<SearchBar
						setState={setCommittedSearch}
						placeholder="Search RFx"
						data-testid="rfx-list-search-input"
					/>
					<SortComp
						data={RFX_SORT_OPTIONS}
						sorted={sorted}
						onClickCB={(item: RfxSortOption) => {
							setOpenSortPopUp(false);
							setSorted(item);
						}}
						openPopUpCB={openSortPopUp}
						setOpenPopUpCB={setOpenSortPopUp}
					/>
					{/* 14.4: cards-only on mobile, so the toggle has nothing to do. */}
					{!isMobile && (
						<ButtonViewGroup
							view={view}
							setView={setView}
						/>
					)}
				</Flex>
			</div>

			{isLoading && (
				<div className="space-y-4" data-testid="rfx-list-loading">
					<SkeletonBase width="100%" height="56px" />
					<SkeletonBase width="100%" height="400px" />
				</div>
			)}

			{!isLoading && isError && (
				<div
					className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
					data-testid="rfx-list-error"
				>
					Unable to load this list. Please try again later.
				</div>
			)}

			{!isLoading && !isError && rows.length === 0 && (
				<div data-testid="rfx-list-empty-state">
					{renderEmptyState ? (
						renderEmptyState()
					) : (
						<p className="text-sm text-gray-500">No results found.</p>
					)}
				</div>
			)}

			{!isLoading && !isError && rows.length > 0 && (
				<>
					{/*
					 * Paging is offered above AND below the results: with 12 cards
					 * per page the controls sit below the fold, so a top copy
					 * saves a scroll-to-bottom-then-back on every page change.
					 * Both drive the same `pagination` state — they are one
					 * control rendered twice, never two independent pagers.
					 */}
					{/*
				 * `Pagination` carries a `border-t` for the bottom placement, where
				 * it separates the controls from the list above. The TOP copy sits
				 * directly under the tab strip, where that same rule drew a second
				 * full-page-width line to the right of the tabs. `!` is required:
				 * `border-t-0` and the base `border-t` have equal specificity, so
				 * without it the winner depends on stylesheet order.
				 */}
				{renderPagination("rfx-list-pagination-top", "!border-t-0")}

					{renderList(rows, meta, effectiveView)}

					{renderPagination("rfx-list-pagination")}
				</>
			)}
		</div>
	);
};
